import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * 🔄 Stale-build guard — some phones (installed PWA / long-lived tabs) keep an
 * old cached index.html+bundle and miss new tabs/features entirely. This hook
 * polls /version.json (cache-bypassed); when a redeploy changes the version it
 * tells the user and hard-refreshes so everyone runs the latest build.
 */
const CHECK_MS = 5 * 60 * 1000;

export default function useVersionGuard() {
  const baseline = useRef(null);

  useEffect(() => {
    let stop = false;
    const fetchV = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        const { v } = await res.json();
        return v || null;
      } catch {
        return null;
      }
    };
    const check = async () => {
      const v = await fetchV();
      if (stop || !v) return;
      if (!baseline.current) {
        baseline.current = v;
        return;
      }
      if (v !== baseline.current) {
        baseline.current = v;
        toast.info("🔄 A new version of VTS is available — updating…", { duration: 2500 });
        setTimeout(() => window.location.reload(), 2600);
      }
    };
    check();
    const iv = setInterval(check, CHECK_MS);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { stop = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, []);
}
